import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { isDemoMode } from '../config/env';
import { authService } from '../services/authService';
import { bootstrapDemoAuth } from '../demo/demoApi';
import type { User, UserRole } from '../types/auth';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithOtp: (payload: {
    phone: string;
    code: string;
    purpose: 'signin' | 'signup';
    name?: string;
    honorific?: 'mr' | 'ms';
    dateOfBirth?: number;
  }) => Promise<void>;
  switchRole: (role: UserRole) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    if (isDemoMode) {
      if (!authService.isAuthenticated()) {
        bootstrapDemoAuth();
      }
      setUser(await authService.getMe());
      return;
    }

    if (!authService.isAuthenticated()) {
      setUser(null);
      return;
    }
    try {
      const me = await authService.getMe();
      setUser(me);
    } catch {
      authService.logout();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    await authService.signIn({ email, password });
    await refreshUser();
  };

  const loginWithOtp = async (payload: {
    phone: string;
    code: string;
    purpose: 'signin' | 'signup';
    name?: string;
    honorific?: 'mr' | 'ms';
    dateOfBirth?: number;
  }) => {
    await authService.verifyOtp(payload);
    await refreshUser();
  };

  const switchRole = async (targetRole: UserRole) => {
    const updated = await authService.switchRole(targetRole);
    setUser(updated);
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    if (isDemoMode) {
      bootstrapDemoAuth();
      refreshUser();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithOtp, switchRole, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
